import { apiFetch } from "@/lib/api";

import type { LaborDomain, LaborTargetDescriptor } from "./laborTarget";

export interface LaborSession {
  id: number;
  started_at: string;
  device_count?: number;
  net_seconds?: number;
  page?: string;
  tab_id?: string | null;
  /** Present when the active-session payload includes `labor.device_session.last_heartbeat_at`. */
  last_heartbeat_at?: string;
}

export interface LaborScan {
  id?: number;
  session_id: number;
  serial_fci_id?: number | null;
  device_serial: string;
  build_order_id?: number | null;
  batch_item_id?: number | null;
  scanned_at?: string;
  allocated_seconds?: number | string | null;
}

export type LaborConflictSource = "device" | "mobile";

export interface LaborActiveSessionPayload {
  session?: LaborSession | null;
  scans?: LaborScan[];
  active_on_other_page?: string;
  active_on_other_source?: LaborConflictSource;
  idle_timeout_ms?: number;
  heartbeat_interval_ms?: number;
  stale_sessions_closed?: number;
  was_stale?: boolean;
  writesBlocked?: boolean;
  writeFirebreak?: boolean;
  error?: string;
  raw?: unknown;
}

export interface LaborStartPayload extends LaborActiveSessionPayload {
  page?: string;
}

export interface LaborEndPayload {
  device_count?: number;
  net_seconds?: number;
  seconds_per_device?: number | string | null;
  writesBlocked?: boolean;
  writeFirebreak?: boolean;
  error?: string;
}

export async function fetchLaborActiveSession(input: {
  page: string;
  domain?: LaborDomain;
}): Promise<LaborActiveSessionPayload> {
  return apiFetch<LaborActiveSessionPayload>("/api/labor/active-session", {
    query: {
      page: input.page.trim() !== "" ? input.page.trim() : "hub-overview",
      ...(input.domain ? { domain: input.domain } : {}),
    },
  });
}

export async function fetchLaborOpenSession(options: { signal?: AbortSignal } = {}): Promise<LaborActiveSessionPayload> {
  return apiFetch<LaborActiveSessionPayload>("/api/labor/open-session", {
    signal: options.signal,
  });
}

export async function startLaborSession(input: {
  page: string;
  domain: LaborDomain;
  tabId?: string | null;
}): Promise<LaborStartPayload> {
  return apiFetch<LaborStartPayload>("/api/labor/start-session", {
    method: "POST",
    body: {
      page: input.page,
      domain: input.domain,
      ...(input.tabId != null && input.tabId.trim() !== "" ? { tabId: input.tabId.trim() } : {}),
    },
  });
}

export async function endLaborSession(input: {
  sessionId: number;
  idleTimeout: boolean;
  domain: LaborDomain;
  keepalive?: boolean;
}): Promise<LaborEndPayload> {
  return apiFetch<LaborEndPayload>("/api/labor/end-session", {
    method: "POST",
    body: { sessionId: input.sessionId, idleTimeout: input.idleTimeout, domain: input.domain },
    keepalive: input.keepalive,
  });
}

export interface LaborEndMobilePayload {
  ok?: boolean;
  ended_count?: number;
  page?: string | null;
  net_seconds?: number;
  writesBlocked?: boolean;
  writeFirebreak?: boolean;
  error?: string;
}

export async function endMobileLaborSession(): Promise<LaborEndMobilePayload> {
  return apiFetch<LaborEndMobilePayload>("/api/labor/end-mobile-session", {
    method: "POST",
    body: {},
  });
}

export async function sendLaborHeartbeat(input: {
  sessionId: number;
  domain: LaborDomain;
}): Promise<Record<string, unknown>> {
  return apiFetch<Record<string, unknown>>("/api/labor/heartbeat", {
    method: "POST",
    body: { sessionId: input.sessionId, domain: input.domain },
  });
}

export async function recordLaborScan(input: {
  sessionId: number;
  deviceSerial: string;
  target: LaborTargetDescriptor;
  domain: LaborDomain;
}): Promise<Record<string, unknown>> {
  return apiFetch<Record<string, unknown>>("/api/labor/record-scan", {
    method: "POST",
    body: {
      sessionId: input.sessionId,
      deviceSerial: input.deviceSerial,
      target: input.target,
      domain: input.domain,
    },
  });
}

export async function recordLaborMcuScans(input: {
  sessionId: number;
  mcuUuids: string[];
  domain: LaborDomain;
}): Promise<{ recorded?: number; writesBlocked?: boolean }> {
  return apiFetch<{ recorded?: number; writesBlocked?: boolean }>("/api/labor/record-mcu-scans", {
    method: "POST",
    body: {
      sessionId: input.sessionId,
      mcuUuids: input.mcuUuids,
      domain: input.domain,
    },
  });
}

export async function reopenIdleLaborSession(input: {
  sessionId: number;
  domain: LaborDomain;
}): Promise<Record<string, unknown>> {
  return apiFetch<Record<string, unknown>>("/api/labor/reopen-idle-session", {
    method: "POST",
    body: {
      sessionId: input.sessionId,
      domain: input.domain,
    },
  });
}

